import {
  DEFAULT_PAGE_SIZE,
  formatParisDate,
  formatParisDateTime,
  UNIFIED_ORDER_STATUS,
  unifiedStatusFromInvoice,
  type PennylaneInvoicePreview,
} from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { CloudDownload, Download, Send, SendHorizontal, XCircle } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { Page, PageHeader, PageSection } from '@/components/layout/page.js';
import { Badge, StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { TimelineEntryRow } from '@/components/ui/timeline.js';
import {
  DetailHeaderSkeleton,
  DetailPanelSkeleton,
} from '@/features/orders/detail-header-skeleton.js';
import { ApiError, downloadFromApi } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { V1Badge, V1Notice } from '@/features/orders/v1-badge.js';
import { fetchInvoiceDetail } from './api.js';
import { IgnoreInvoiceDialog } from './ignore-invoice-dialog.js';
import { useForceSendInvoice, useIgnoreInvoice, useRefreshInvoice, useResendInvoice } from './invoice-actions.js';

/** Squelette à la forme de la fiche : en-tête puis panneaux « Envoi » et « Historique ». */
function InvoiceDetailSkeleton(): JSX.Element {
  return (
    <Page width="detail">
      <DetailHeaderSkeleton />
      <DetailPanelSkeleton lines={4} />
      <DetailPanelSkeleton />
    </Page>
  );
}

const PREVIEW_VERDICT: Record<PennylaneInvoicePreview['decision'], string> = {
  ready: 'La facture peut être renvoyée à Martelet.',
  ignored: 'La facture serait ignorée',
  error: 'La facture resterait en erreur',
};

/**
 * Résultat de la dernière « Mise à jour depuis Pennylane » (`02` §4) : ce que
 * donnerait un renvoi maintenant, et le CSV qui partirait. Rien n'a été envoyé.
 */
function InvoicePreviewSection({ preview }: { preview: PennylaneInvoicePreview }): JSX.Element {
  return (
    <PageSection
      title="Aperçu CSV"
      description={`Calculé le ${formatParisDateTime(new Date(preview.calculatedAt))} avec les données Pennylane les plus récentes, non envoyé.`}
    >
      <div className="flex flex-col gap-3 text-sm">
        <p className={preview.decision === 'ready' ? 'font-medium text-status-green-fg' : 'break-words'}>
          {preview.decision === 'ready'
            ? PREVIEW_VERDICT.ready
            : `${PREVIEW_VERDICT[preview.decision]}${preview.reason ? ` : ${preview.reason}` : '.'}`}
        </p>
        {preview.csvContent ? (
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 font-mono text-xs">
            {preview.csvContent}
          </pre>
        ) : null}
      </div>
    </PageSection>
  );
}

/**
 * Fiche d'une facture Pennylane (`02` §3), ouverte depuis la liste Commandes.
 * Pas de dialogue : c'est une page à part entière, comme le détail d'une
 * commande — `routes/commande-facture-detail.tsx`.
 */
export function InvoiceDetailPage(): JSX.Element {
  const params: { id?: string } = useParams({ strict: false });
  const id = params.id ?? '';
  const [confirmResend, setConfirmResend] = useState(false);
  const [ignoreOpen, setIgnoreOpen] = useState(false);

  const query = useQuery({
    queryKey: queryKeys.invoices.detail(id),
    queryFn: () => fetchInvoiceDetail(id),
    enabled: id !== '',
  });

  const refresh = useRefreshInvoice();
  const resend = useResendInvoice();
  const force = useForceSendInvoice();
  const ignore = useIgnoreInvoice();

  if (query.isPending) {
    return <InvoiceDetailSkeleton />;
  }

  if (query.isError || !query.data) {
    return (
      <Page width="detail">
        <ErrorState
          message={query.error instanceof ApiError ? query.error.message : 'Facture introuvable.'}
          detail={query.error instanceof ApiError ? query.error.detail : undefined}
          onRetry={() => void query.refetch()}
        />
      </Page>
    );
  }

  const invoice = query.data;
  const alreadySent = invoice.status === 'sent';

  return (
    <Page width="detail">
      <PageHeader
        backLink={{
          label: 'Retour aux commandes',
          to: '/commandes',
          search: { page: 1, pageSize: DEFAULT_PAGE_SIZE, sortDir: 'desc' },
        }}
        title={`Facture ${invoice.invoiceNumber || invoice.invoiceId}`}
        status={<span className="flex items-center gap-2"><StatusBadge registry={UNIFIED_ORDER_STATUS} code={(invoice.simulated || invoice.handledByV1?.transmission) && invoice.status === 'pending' ? 'transmitted' : unifiedStatusFromInvoice(invoice.status)} />{invoice.simulated ? <Badge tone="orange">Simulé</Badge> : null}<V1Badge handledByV1={invoice.handledByV1} /></span>}
        description={
          <span className="flex flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span>Pennylane</span>
              <span aria-hidden="true">·</span>
              <span className="tabular-nums">
                {formatParisDate(new Date(`${invoice.date}T00:00:00.000Z`))}
              </span>
              {invoice.customerName ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="min-w-0 break-words">{invoice.customerName}</span>
                </>
              ) : null}
              {invoice.amount ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="font-medium tabular-nums text-foreground">{invoice.amount} €</span>
                </>
              ) : null}
            </span>
            <V1Notice handledByV1={invoice.handledByV1} />
          </span>
        }
        actions={
          <>
            {invoice.fileId ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  downloadFromApi(`/api/files/${invoice.fileId ?? ''}/download`);
                }}
              >
                <Download aria-hidden="true" />
                Télécharger le CSV
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              isLoading={refresh.isPending}
              onClick={() => {
                refresh.mutate(invoice.id);
              }}
            >
              <CloudDownload aria-hidden="true" />
              Mettre à jour depuis Pennylane
            </Button>
            {invoice.status !== 'queued' ? (
              <Button
                type="button"
                variant="outline"
                isLoading={resend.isPending}
                onClick={() => {
                  if (alreadySent) {
                    setConfirmResend(true);
                  } else {
                    resend.mutate({ id: invoice.id, confirmResend: false });
                  }
                }}
              >
                <SendHorizontal aria-hidden="true" />
                Renvoyer la facture à Martelet
              </Button>
            ) : null}
            {invoice.status === 'error' ? (
              <Button
                type="button"
                variant="outline"
                isLoading={force.isPending}
                onClick={() => {
                  force.mutate(invoice.id);
                }}
              >
                <Send aria-hidden="true" />
                Forcer l’envoi
              </Button>
            ) : null}
            {invoice.status !== 'ignored' ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIgnoreOpen(true);
                }}
              >
                <XCircle aria-hidden="true" />
                Ignorer
              </Button>
            ) : null}
          </>
        }
      />

      {invoice.reason || invoice.lastError ? (
        <PageSection title={invoice.status === 'error' ? 'Erreur' : 'Motif'}>
          <div className="flex flex-col gap-1 text-sm">
            {invoice.reason ? <p className="break-words">{invoice.reason}</p> : null}
            {invoice.lastError ? (
              <p className="break-words text-status-red-fg">{invoice.lastError}</p>
            ) : null}
          </div>
        </PageSection>
      ) : null}

      {invoice.preview ? <InvoicePreviewSection preview={invoice.preview} /> : null}

      <PageSection title="Envoi">
        <DescriptionList>
          <DescriptionItem label="Fichier">
            <span className="break-all">{invoice.fileName ?? '—'}</span>
          </DescriptionItem>
          <DescriptionItem label="Transmise le">
            <span className="tabular-nums">
              {invoice.sentAt ? formatParisDateTime(new Date(invoice.sentAt)) : '—'}
            </span>
          </DescriptionItem>
          <DescriptionItem label="Tentatives">
            <span className="tabular-nums">{invoice.attempts}</span>
          </DescriptionItem>
          <DescriptionItem label="Lignes">
            <span className="tabular-nums">
              {invoice.lineStats
                ? `${String(invoice.lineStats.emitted)} émise(s) sur ${String(invoice.lineStats.api)}`
                : '—'}
            </span>
          </DescriptionItem>
        </DescriptionList>
      </PageSection>

      <PageSection title="Historique">
        {invoice.timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun événement enregistré.</p>
        ) : (
          <ol className="flex flex-col">
            {invoice.timeline.map((entry, index) => (
              <TimelineEntryRow key={`${entry.at}-${String(index)}`} entry={entry} />
            ))}
          </ol>
        )}
      </PageSection>

      <ConfirmDialog
        open={confirmResend}
        onOpenChange={setConfirmResend}
        title="Renvoyer la facture à Martelet"
        description={`La facture ${invoice.invoiceNumber || invoice.invoiceId} sera relue dans Pennylane, puis un nouveau fichier sera généré et envoyé. Elle a déjà été transmise à Martelet : si le premier envoi a été intégré, elle peut y être en double.`}
        confirmLabel="Renvoyer"
        isConfirming={resend.isPending}
        onConfirm={() => {
          resend.mutate(
            { id: invoice.id, confirmResend: true },
            {
              onSuccess: () => {
                setConfirmResend(false);
              },
            },
          );
        }}
      />

      <IgnoreInvoiceDialog
        open={ignoreOpen}
        onOpenChange={setIgnoreOpen}
        description={`La facture ${invoice.invoiceNumber || invoice.invoiceId} ne sera pas transmise à Martelet.`}
        isSubmitting={ignore.isPending}
        onConfirm={(reason) => {
          ignore.mutate(
            { id: invoice.id, reason },
            {
              onSuccess: () => {
                setIgnoreOpen(false);
              },
            },
          );
        }}
      />
    </Page>
  );
}
